import { defineStore } from 'pinia'
import { fetchStories, getSiteProperties } from '../api/storyblok'
import {
  fetchVacancy,
  fetchVacancyList,
  fetchCompany,
  fetchSite,
  fetchTagList,
  fetchCompanyList,
  fetchLocationList,
} from '../api/tooContentDelivery'
import {
  getVacancyIdFromPath,
  getJobAlertTokenFromPath,
  getCompanySlugFromPath,
} from '../utils/extractFromPath'
import {
  isVacancyActive,
  normalizeHeadingsToH2Max,
  pickPublicationScope,
} from '../utils/vacancyUtils.js'
import { saveFavorites, saveSearches } from '../utils/userUtils.js'
import hydrateObject from '../utils/hydrateObject.js'
import { redirect, render } from 'vike/abort'
import { generateFontObject } from '../utils/fontUtils.js'
import netherlandsRegions from '../utils/netherlandsRegions.js'
import { deepEqual } from 'fast-equals'

export const useMainStore = defineStore('main', {
  state: () => ({
    domainSlug: '',
    site: {},
    theme: null,

    pageContent: null,
    pageContext: null,

    vacancy: null,
    company: null,
    publication: null,
    region: null,

    // Can be used as a badge in links and buttons
    siteVacanciesTotal: 0,
    siteVacanciesFetched: false,

    // Used for list blocks (VacancyList & CompanyList) to prefetch data server-side, before page render
    prefetchedData: {},

    // Used for A/B testing
    features: {},

    // Used only in sites that have authorization enabled
    authInitialized: false,
    authCheckFinished: false,
    auth: null,
    userEmail: null,
    userToken: null,

    // These user fields are saved in localStorage
    favorites: [],
    recentSearches: [],
  }),
  getters: {
    abTestBlocks: (state) => {
      return (
        state.pageContent?.blocks.filter(
          (block) => block.component === 'ABTest',
        ) || []
      )
    },
    blockRobots: (state) => {
      if (
        import.meta.env.VITE_BASE_TOO_API_URL.includes(
          'staging.jobsrepublictoo',
        )
      ) {
        return true
      }
      if (
        state.pageContent?.blockIndexing ||
        state.pageContent?.authenticationRequired
      ) {
        return true
      }

      if (
        state.pageContent?.slug.includes('/error-404') ||
        state.pageContent?.slug.includes('/_default/_unsubscribe') ||
        state.pageContent?.slug.includes('/_default/_jobalert')
      ) {
        return true
      }

      if (
        state.publication?.end &&
        state.pageContent?.slug.includes('/_default/_vacancy')
      ) {
        return Date.now() > Date.parse(state.publication.end)
      }

      return false
    },
    themeVariables: (state) => {
      const legacyThemeVariables = state.theme || state.site?.theme || {}
      const legacyColorPrefixes = [
        '--primary',
        '--secondary',
        '--tertiary',
        '--fourth',
        '--gray',
      ]

      const themeVariables = {}

      let includesLegacyColors = false
      for (const [key, value] of Object.entries(legacyThemeVariables)) {
        if (key.startsWith('--')) {
          // We also use variables for handling fonts, so we need to check if it's a color variable
          const isLegacyColor = legacyColorPrefixes.find((prefix) =>
            key.startsWith(prefix),
          )

          if (
            !isLegacyColor ||
            !legacyThemeVariables?.dontUseDeprecatedColors
          ) {
            themeVariables[key] = value

            if (key.includes('primary-500')) {
              includesLegacyColors = true
              themeVariables['--default'] = value
            }
          }
        }
      }

      state.theme?.palette?.forEach((color) => {
        themeVariables[`--${color.name}`] = color.value

        if (!includesLegacyColors && color.default) {
          themeVariables['--default'] = color.value
          // TODO Improve handling of secondary color
          // A small group of blocks have the secondary color hardcoded for their links/buttons
          themeVariables['--secondary-500'] = color.value
          themeVariables['--secondary-600'] = color.value
        }
      })

      // Add grey scale if it's not defined
      if (!includesLegacyColors) {
        // This colors are from the color scheme of Gemeentebanen, used by default in the previous theme
        themeVariables['--gray-100'] = '#efefef'
        themeVariables['--gray-200'] = '#dfdfdf'
        themeVariables['--gray-300'] = '#d0d0d0'
        themeVariables['--gray-400'] = '#c0c0c0'
        themeVariables['--gray-500'] = '#b0b0b0'
        themeVariables['--gray-600'] = '#8d8d8d'

        if (themeVariables['--dark-text']) {
          themeVariables['--gray-700'] =
            `color-mix(in oklab, ${themeVariables['--dark-text']}, white 20%)`
          themeVariables['--gray-800'] = themeVariables['--dark-text']
          themeVariables['--gray-900'] =
            `color-mix(in oklab, ${themeVariables['--dark-text']}, black 35%)`
        } else {
          themeVariables['--gray-700'] = '#6a6a6a'
          themeVariables['--gray-800'] = '#464646'
          themeVariables['--gray-900'] = '#232323'
        }
      }

      // We add the default colors
      themeVariables['--black'] = '#000000'
      themeVariables['--white'] = '#FFFFFF'
      themeVariables['--transparent'] = 'transparent'

      return themeVariables
    },
  },
  actions: {
    async fetchPage(context) {
      const storiesToFetch = ['/_theme']
      // We add the specific path
      storiesToFetch.push(context.path.split('?')[0])

      // We extract the entities ids from the URL
      let region
      let vacancyId = getVacancyIdFromPath(
        context.path,
        this.site?.vacancyLinkFormat,
      )
      let companySlug = getCompanySlugFromPath(
        context.path,
        vacancyId ? this.site?.vacancyLinkFormat : this.site?.companyLinkFormat,
      )
      const { token, action } = getJobAlertTokenFromPath(context.path)

      const enhancedContext = {
        ...context,
        domainSlug: this.domainSlug,
        baseTooUrl: import.meta.env.VITE_BASE_TOO_API_URL,
        vacancyId,
        jobAlertToken: token,
      }

      let vacancySearchPage = false
      if (vacancyId) {
        // We need to remove the vacancy slug for searching the specific page on Storyblok
        storiesToFetch.push(context.path.split(vacancyId)[0] + vacancyId)
        storiesToFetch.push('/_default/_vacancy')
      } else if (companySlug) {
        storiesToFetch.push('/_default/_company')
      } else if (token) {
        if (action === 'update') {
          storiesToFetch.push('/_default/_jobalert')
        } else if (action === 'delete') {
          storiesToFetch.push('/_default/_unsubscribe')
        }
      } else {
        const isJobBoard = [
          'local-dev-io',
          'gemeentebanen-nl',
          'zorgbanen-nl',
          'werkenvoorlimburg-nl',
        ].some((jobBoard) => this.domainSlug.includes(jobBoard))

        // All pages with format /vacatures/<slug> are search by category pages, in job boards
        if (isJobBoard && context.path.match(/\/vacatures\/\D+/)) {
          vacancySearchPage = true

          const { template, searchParams } =
            await this.fetchVacancySearchTemplate(
              context.path,
              context.storyblokVersion,
            )
          if (template) {
            storiesToFetch.push(template)
            enhancedContext.searchParams = searchParams
            region = searchParams.region
          }
        }
      }
      storiesToFetch.push('/error-404')

      const [stories, tooData] = await Promise.all([
        fetchStories(
          storiesToFetch.map((slug) => `${this.domainSlug}${slug}`),
          context.storyblokVersion,
        ),
        this.fetchTooData({
          vacancyId,
          companySlug,
        }),
      ])
      let vacancy = tooData?.vacancy
      let company = tooData?.company
      let publication = tooData?.publication

      let theme, pageContent, errorPage
      stories.forEach((story) => {
        if (story.slug.includes('/_theme')) {
          const themeFonts = generateFontObject(story)
          theme = {
            ...story.themeColors.colors,
            ...themeFonts,
            palette: story.palette,
            dontUseDeprecatedColors: story.dontUseDeprecatedColors,
            locale: story.locale || 'nl',
            notFormatCurrency: story.notFormatCurrency,
          }
        } else if (story.slug.includes('/error-404')) {
          errorPage = story
        } else if (!pageContent) {
          pageContent = story
        }
      })

      if (!pageContent && !import.meta.env.VITE_IS_EDITOR) {
        if (vacancySearchPage) {
          throw redirect('/vacatures', 301)
        } else if (errorPage) {
          pageContent = errorPage
        } else {
          throw render(404, 'Page not found')
        }
      }

      if (pageContent.dataVacancyId) {
        vacancyId = pageContent.dataVacancyId
      }
      if (pageContent.dataCompanySlug) {
        companySlug = pageContent.dataCompanySlug
      } else if (
        companySlug &&
        !pageContent.slug.includes('/_default/_company')
      ) {
        // If we're not using the company template, and there's no company slug set at Page level, we ignore the company slug
        // we extracted from the url
        companySlug = null
      }

      if (
        (pageContent.dataVacancyId &&
          pageContent.dataVacancyId != vacancy?.id) ||
        (pageContent.dataCompanySlug &&
          pageContent.dataCompanySlug !== company?.slug)
      ) {
        if (vacancy?.id || company?.slug) {
          // We need fetch TOO data again
          console.log(
            `Page level params are different to the ones extracted from the URL, fetching TOO data again`,
          )
        }

        const newTooData = await this.fetchTooData({
          vacancyId,
          companySlug,
        })
        vacancy = newTooData?.vacancy
        company = newTooData?.company
        publication = newTooData?.publication
      }

      if (vacancy) {
        region = vacancy.regions?.length ? vacancy.regions[0].slug : null
      } else if (company) {
        const regionId = company.location?.region
        if (regionId) {
          region = netherlandsRegions[regionId]?.slug || null
        }
      }

      const vacancyAvailable = isVacancyActive(
        vacancy,
        pageContent.slug,
        this.site.publicationGracePeriodInDays,
      )

      if (!import.meta.env.VITE_IS_EDITOR) {
        // Go to 404 page if we can't find the vacancy and there's a vacancy ID available
        if (vacancyId && !vacancyAvailable) {
          if (pageContent.redirectUrl) {
            const hydratedUrl = hydrateObject(
              { url: pageContent.redirectUrl },
              { vacancy, region },
            )
            throw redirect(hydratedUrl.url, 301)
          } else {
            console.log(
              `Vacancy with ID ${vacancyId} not found, display the 404 page`,
            )
            pageContent = errorPage
          }
        } else if (companySlug && !company) {
          // Do the same thing for missing company pages
          if (pageContent.redirectUrl) {
            throw redirect(pageContent.redirectUrl, 301)
          } else {
            console.log(
              `Company with slug ${companySlug} not found, display the 404 page`,
            )
            pageContent = errorPage
          }
        }
      }

      if (!pageContent) {
        throw render(404, 'Page not found')
      }

      // We update the store after all requests are done so we don't trigger multiple changes in the UI
      this.$patch({
        pageContent,
        pageContext: enhancedContext,
        theme,
        vacancy,
        company,
        publication,
        region,
      })
    },
    async fetchVacancySearchTemplate(path) {
      let searchParams = {}
      let template

      if (path.match(/\/vacatures\/\D+/)) {
        const pathname = new URL(path, 'http://localhost').pathname
        const segments = pathname.split('/').filter(Boolean)
        const vacaturesIndex = segments.indexOf('vacatures')
        if (vacaturesIndex !== -1 && vacaturesIndex + 1 < segments.length) {
          const [props, tagGroups] = await Promise.all([
            getSiteProperties(this.domainSlug),
            fetchTagList({
              domainSlug: this.domainSlug,
              baseTooUrl: import.meta.env.VITE_BASE_TOO_API_URL,
            }),
          ])

          const categorySlug = segments[vacaturesIndex + 1]
          let category = tagGroups.reduce((acc, group) => {
            if (!acc) {
              const tag = group.tags.find((item) => item.key === categorySlug)
              if (tag) {
                acc = tag
              }
            }
            return acc
          }, null)

          if (!category) {
            const extraSearchCategoriesStr = props['search_extra_categories']
            if (extraSearchCategoriesStr) {
              const extraSearchCategories = extraSearchCategoriesStr
                .replaceAll(', ', ',')
                .split(',')
              if (extraSearchCategories.includes('location')) {
                const locations = await fetchLocationList({
                  domainSlug: this.domainSlug,
                  baseTooUrl: import.meta.env.VITE_BASE_TOO_API_URL,
                })
                const location = locations.find(
                  (item) => item.slug === categorySlug,
                )
                if (location) {
                  category = {
                    tagCategory: 'LOCATION',
                    label: location.name,
                    key: location.slug,
                    geoLocation: {
                      label: location.name,
                      latitude: location.latitude,
                      longitude: location.longitude,
                      distance: location.radius,
                    },
                    region: location.region.slug,
                  }
                }
              }
              if (!category && extraSearchCategories.includes('company')) {
                const { data } = await fetchCompanyList({
                  searchParams: {
                    filter: categorySlug,
                  },
                  page: {
                    page: 1,
                    size: 999,
                  },
                  domainSlug: this.domainSlug,
                  baseTooUrl: import.meta.env.VITE_BASE_TOO_API_URL,
                })
                const company = data.find((item) => item.slug === categorySlug)
                if (company) {
                  category = {
                    tagCategory: 'COMPANY',
                    label: company.name,
                    key: company.id,
                  }
                }
              }
            }
          }

          if (category) {
            searchParams = category
            switch (category.tagCategory) {
              case 'REGION':
                template = '/_default/_search_by_region'
                searchParams.region = searchParams.key
                break
              case 'LOCATION':
                template = '/_default/_search_by_location'
                break
              case 'COMPANY':
                template = '/_default/_search_by_company'
                break
              case 'VACANCY_EDUCATION':
                template = '/_default/_search_by_education'
                break
              case 'VACANCY_EMPLOYMENTTYPE':
                template = '/_default/_search_by_employment_type'
                break
              case 'VACANCY_WORKCATEGORY':
                template = '/_default/_search_by_work_category'
                break
              case 'VACANCY_WORKEXPERIENCE':
                template = '/_default/_search_by_work_experience'
                break
              case 'VACANCY_WORKFIELD':
                template = '/_default/_search_by_discipline'
                break
            }
          }

          searchParams.defaultSearchTitle = props['search_default_title']
          searchParams.defaultSearchSubtitle = props['search_default_subtitle']

          const labelsStr =
            props[`search_${searchParams.key || searchParams.slug}`]
          if (labelsStr) {
            const labels = labelsStr.replaceAll(' | ', '|').split('|')
            if (labels.length === 3) {
              searchParams.searchTitle = labels[0]
              searchParams.searchSubtitle = labels[1]
              searchParams.searchDescription = labels[2]
            } else if (labels.length === 2) {
              searchParams.searchSubtitle = labels[0]
              searchParams.searchDescription = labels[1]
            } else if (labels.length === 1) {
              searchParams.searchSubtitle = labels[0]
            }
          }
          // We use the default copy for each section
          switch (searchParams.tagCategory) {
            case 'REGION':
            case 'LOCATION':
              searchParams.searchTitle =
                searchParams.searchTitle || `Vacatures in ${searchParams.label}`
              searchParams.searchSubtitle =
                searchParams.searchSubtitle ||
                searchParams.defaultSearchSubtitle
              break
            case 'COMPANY':
              searchParams.searchTitle =
                searchParams.searchTitle ||
                `Vacatures voor ${searchParams.label}`
              searchParams.searchSubtitle =
                searchParams.searchSubtitle ||
                searchParams.defaultSearchSubtitle
              break
            default:
              searchParams.searchTitle =
                searchParams.searchTitle || `${searchParams.label} vacatures`
              searchParams.searchSubtitle =
                searchParams.searchSubtitle ||
                searchParams.defaultSearchSubtitle
              break
          }
        }
      }

      return { template, searchParams }
    },
    async fetchSite() {
      if (this.site.name) return

      try {
        const data = await fetchSite({
          domainSlug: this.domainSlug,
          baseTooUrl: import.meta.env.VITE_BASE_TOO_API_URL,
        })

        // theme is provided as a stringified JSON, so we convert it to a JS object
        if (data.theme && typeof data.theme === 'string') {
          data.theme = JSON.parse(data.theme)
        }
        this.site = data
      } catch (err) {
        console.error(err)
      }
    },
    async fetchTooData({ vacancyId, companySlug }) {
      const fetchPromises = []

      let vacancyData,
        companyData,
        publicationData = null

      try {
        if (companySlug) {
          fetchPromises.push(
            fetchCompany({
              companySlug,
              domainSlug: this.domainSlug,
              baseTooUrl: import.meta.env.VITE_BASE_TOO_API_URL,
            }).then((company) => {
              companyData = company
            }),
          )
        }
        if (vacancyId) {
          fetchPromises.push(
            fetchVacancy({
              vacancyId,
              jwt: null, // This runs on the server, so the user is not authenticated yet
              domainSlug: this.domainSlug,
              baseTooUrl: import.meta.env.VITE_BASE_TOO_API_URL,
            }).then((vacancy) => {
              vacancyData = vacancy
              vacancyData.description = normalizeHeadingsToH2Max(
                vacancyData.description || '',
              )
              addContactFirstAndLastName(vacancyData)
              formatVacancySalaries(vacancyData, this.theme)
              publicationData = pickPublicationScope(vacancyData)

              if (!companySlug) {
                return fetchCompany({
                  companySlug: vacancyData.company.slug,
                  domainSlug: this.domainSlug,
                  baseTooUrl: import.meta.env.VITE_BASE_TOO_API_URL,
                }).then((company) => {
                  companyData = company
                })
              }
            }),
          )
        }

        const results = await Promise.allSettled(fetchPromises)
        results.forEach((result) => {
          if (result.status === 'rejected') {
            console.error(result.reason)
          }
        })

        return {
          vacancy: vacancyData,
          company: companyData,
          publication: publicationData,
        }
      } catch (err) {
        console.error(err)
      }
    },
    clearContextualSearch() {
      if (this.pageContext?.searchParams) {
        this.pageContext = {
          ...this.pageContext,
          searchParams: {
            searchTitle: this.pageContext.searchParams.defaultSearchTitle,
            searchSubtitle: this.pageContext.searchParams.defaultSearchSubtitle,
          },
        }

        // We need to clear also the prefetched data, because changing the context triggers a re render of the entire page
        this.prefetchedData = {}
      }
    },
    getTotalSiteVacancies() {
      if (this.siteVacanciesFetched) {
        return
      }

      this.siteVacanciesFetched = true
      fetchVacancyList({
        searchParams: {},
        page: {
          page: 1,
          size: 999,
        },
        sortBy: 'default',
        jwt: this.userToken,
        domainSlug: this.domainSlug,
        baseTooUrl: import.meta.env.VITE_BASE_TOO_API_URL,
      }).then(({ data }) => {
        this.siteVacanciesTotal = data?.length || 0
      })
    },
    async initAuthentication(isCallback) {
      if (this.authInitialized) {
        return
      }

      this.authInitialized = true

      const [props, { default: auth0 }] = await Promise.all([
        getSiteProperties(this.domainSlug),
        import('auth0-js'),
      ])

      // We check that the site has configured all the auth properties required
      if (!props.auth_domain || !props.auth_client_id || !props.auth_audience) {
        console.error(
          'Missing configuration, authentication could not be initialized.',
        )
        // TODO Send specific error to Sentry?
        this.authCheckFinished = true
        return false
      }

      return new Promise((resolve) => {
        this.auth = new auth0.WebAuth({
          domain: props.auth_domain,
          clientID: props.auth_client_id,
          responseType: 'token id_token',
          scope: 'openid email',
          redirectUri: `${window.location.origin}/sb-callback`,
          audience: props.auth_audience,
        })

        if (!isCallback) {
          this.auth.checkSession({}, (err, authResult) => {
            if (err) {
              console.log(err)
              this.authCheckFinished = true
              resolve(true)
            } else {
              this.auth.client.userInfo(
                authResult.accessToken,
                async (err, user) => {
                  if (err) {
                    console.log(err)
                  } else {
                    this.userEmail = user.email
                    this.userToken = authResult.accessToken
                  }

                  this.authCheckFinished = true
                  resolve(true)
                },
              )
            }
          })
        } else {
          this.auth.parseHash({ hash: window.location.hash }, (err) => {
            if (err) {
              console.log(err)
            } else {
              const savedState = sessionStorage.getItem('passwordlessState')
              if (savedState) {
                const customData = JSON.parse(savedState)
                if (customData.returnTo) {
                  window.location.href = customData.returnTo
                } else {
                  window.location = '/'
                }
                sessionStorage.removeItem('passwordlessState')
              } else {
                window.location = '/'
              }
            }
            resolve(true)
          })
        }
      })
    },
    logout() {
      this.auth?.logout({ returnTo: window.location.origin })
      this.userEmail = null
      this.userToken = null
    },
    toggleFavorite(vacancy) {
      const index = this.favorites.findIndex(
        (favorite) => favorite.id === vacancy.id,
      )
      if (index > -1) {
        this.favorites.splice(index, 1)
      } else {
        // We use this structure to keep this backwards compatible
        this.favorites.push({
          id: vacancy.id,
          title: vacancy.title,
          organization: vacancy.company.title,
          vacancySlug: vacancy.slug,
          companySlug: vacancy.company.slug,
        })
      }
      // Save to local storage
      saveFavorites(this.favorites)
    },
    addRecentSearch(search) {
      if (search) {
        // We use this structure to keep this backwards compatible
        const compatibleSearch = {
          search_term: search.filter,
        }

        const geoLocation = search.geoLocation
        if (geoLocation?.latitude && geoLocation?.longitude) {
          compatibleSearch.location = geoLocation.label
          compatibleSearch.latitude = geoLocation.latitude
          compatibleSearch.longitude = geoLocation.longitude
          compatibleSearch.distance = geoLocation.distance
        }

        // We check that this search isn't already saved
        const cloneIndex = this.recentSearches.findIndex((rs) =>
          deepEqual(rs, compatibleSearch),
        )
        if (cloneIndex > -1) {
          this.recentSearches.splice(cloneIndex, 1)
        } else if (this.recentSearches.length === 5) {
          this.recentSearches.splice(4, 1)
        }
        this.recentSearches.unshift(compatibleSearch)
        // Save to local storage
        saveSearches(this.recentSearches)
      }
    },
    removeRecentSearch(searchIndex) {
      this.recentSearches.splice(searchIndex, 1)
      // Save to local storage
      saveSearches(this.recentSearches)
    },
  },
})

const addContactFirstAndLastName = (vacancy) => {
  if (vacancy?.contactPerson?.name) {
    const nameParts = vacancy?.contactPerson?.name.split(' ')
    vacancy.contactPerson = {
      ...vacancy.contactPerson,
      firstName: nameParts[0],
      lastName: nameParts.slice(1).join(' '),
    }
  }
}

const formatVacancySalaries = (vacancy, theme) => {
  if (vacancy?.salary) {
    const salary = { ...vacancy.salary }
    const locale = theme?.locale || 'nl'

    salary.minFormatted = !theme?.notFormatCurrency
      ? new Intl.NumberFormat(locale).format(salary.min)
      : salary.min
    salary.maxFormatted = !theme?.notFormatCurrency
      ? new Intl.NumberFormat(locale).format(salary.max)
      : salary.max

    vacancy.salary = salary
  }
}
